import classNames from 'classnames'
import React, {
  ChangeEventHandler,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import type ReCAPTCHADefinition from 'react-google-recaptcha'
import InView from 'react-intersection-observer'
import siteConfig from '../../gatsby/siteConfig'
import { useTranslations } from '../../hooks/useTranslations'
import { PortableTextBodyElement } from '../../utilities/blockContent'
import { getCookieValue } from '../../utilities/cookie'
import { isBrowser } from '../../utilities/isBrowser'
import {
  IS_EQT_FOUNDATION,
  IS_EQT_GROUP,
  IS_EQT_NEXUS,
  IS_EQT_NEXUS_AU,
} from '../../utilities/site'
import { isNullOrWhiteSpace } from '../../utilities/string'
import { Button, ButtonTheme } from '../Button'
import { Checkbox } from '../Forms/Checkbox'
import { Dropdown } from '../Forms/Dropdown'
import { Input } from '../Forms/Input'
import { RadioButton } from '../Forms/RadioButton'
import { RichTextArea } from '../PortableText/RichTextArea'
import { BasicBlockSerializer } from '../PortableText/serializers/blocks/BasicBlockSerializer'
import useNexusFormFields from './useNexusFormFields'

// Lazy-load recaptcha when the user navigates to the SignupForm, to not affect page load speed
const ReCAPTCHA = React.lazy(() => import('react-google-recaptcha'))

interface FilterState {
  [key: string]: boolean
}

interface SignupFormProps {
  disclaimer?: PortableTextBodyElement[]
  theme?: ThemeSettings
}

interface ThemeSettings {
  theme: 'light' | 'dark'
  textColor?: string
  textSize?: string
  radioCheckBoxAlignment?: 'flex-col' | 'flex-col sm:flex-row'
  countryAndInvestorAsColumn?: boolean
}

/**
 * Valid Cision form fields. Note that some/all are also reused for MailChimp,
 * any differences in field names is handled in the associated subscription GCP function.
 */
const CisionFields = {
  filter: 'informationtype',
  language: 'language',
  email: 'email',
  siteName: 'siteName',
  investorType: 'investorType',
  country: 'country',
}

/**
 * informationtype codes (used by SignUpForm end point)
 * */
const CisionFilterValues = {
  eqtNews: 'nbr', // newsletters for EQT Group & all cision press releases for EQT Foundation
  regulatoryNews: 'prm', // regulatory press releases
}

const subscriptionConfig = {
  RecaptchaKey: siteConfig.CisionReCaptchaKey,
  SubscriptionServiceUrl: siteConfig.SubscriptionServiceUrl ?? '',
}

const getThemeSettings = (settings?: ThemeSettings): ThemeSettings => {
  let themeColors: ThemeSettings = {
    theme: 'light',
    textColor: 'text-secondary-darker',
    textSize: 'text-t-200',
    radioCheckBoxAlignment: 'flex-col',
    countryAndInvestorAsColumn: false,
  }

  if (!!settings) {
    if (isNullOrWhiteSpace(settings.textSize) && settings.theme === 'dark') {
      settings.textColor = 'text-white'
    }
    themeColors = { ...themeColors, ...settings }
  }

  return themeColors
}

export const SignupForm = ({ disclaimer, theme }: SignupFormProps): JSX.Element => {
  const translations = useTranslations()
  const recaptchaRef = useRef<ReCAPTCHADefinition>(null)
  const [emailValue, setEmailValue] = useState<string>('')
  const [themeSettings, setThemeSettings] = useState<ThemeSettings>(
    getThemeSettings(theme)
  )
  const [filterState, setFilterState] = useState<FilterState>({})
  const [selectedLanguage, setSelectedLanguage] = useState<string>(
    translations.language ?? 'en'
  )

  const { countries, investors } = useNexusFormFields()
  const [selectedInvestorType, setSelectedInvestorType] = useState<string | undefined>()
  const [selectedCountry, setSelectedCountry] = useState<string | undefined>()

  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null)
  const [resultMessage, setResultMessage] = useState<string>('')
  const [isProcessing, setIsProcessing] = useState<boolean>(false)
  const [submitDisabled, setSubmitDisabled] = useState<boolean>(false)

  const messages = {
    success: translations.subscriptionSuccess,
    alreadySignedUp: translations.subscriptionAlreadySignedUp,
    registrationFailure: translations.subscriptionFail,
    generalError: translations.generalError,
    missingRecaptcha: translations.recaptchaMissing,
  }

  const getSelectedFilters = (): string[] => {
    const filterValues = Object.keys(filterState).filter(key => filterState[key])

    if (filterValues.length === 0) {
      // none selected = all
      return IS_EQT_FOUNDATION
        ? [CisionFilterValues.eqtNews]
        : [CisionFilterValues.eqtNews, CisionFilterValues.regulatoryNews]
    }

    return filterValues
  }

  useEffect(() => {
    setThemeSettings(getThemeSettings(theme))
  }, [theme])

  const formUrlEncode = (key: string, value: string): string =>
    `${encodeURIComponent(key)}=${encodeURIComponent(value)}`

  const getFormContent = (): string => {
    const values: string[] = getSelectedFilters().map(filter =>
      formUrlEncode(CisionFields.filter, filter)
    )

    let site = ''
    if (IS_EQT_FOUNDATION) {
      site = 'foundation'
    }
    if (IS_EQT_NEXUS) {
      site = IS_EQT_NEXUS_AU ? 'nexus_au' : 'nexus'
    }

    values.push(
      formUrlEncode(CisionFields.language, selectedLanguage),
      formUrlEncode(CisionFields.email, emailValue),
      formUrlEncode(CisionFields.siteName, site)
    )

    if (IS_EQT_NEXUS || IS_EQT_NEXUS_AU) {
      values.push(
        formUrlEncode(CisionFields.investorType, selectedInvestorType ?? ''),
        formUrlEncode(CisionFields.country, selectedCountry ?? '')
      )
    }

    values.push(`g-recaptcha-response=${recaptchaToken}`)
    return values.join('&')
  }

  const handleFilterChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
    e => {
      const checked = e.currentTarget.checked
      const filterValue = e.currentTarget.value

      const newState = { ...filterState, [filterValue]: checked }
      setFilterState(newState)
    },
    [filterState]
  )

  const handleLanguageChange = useCallback<ChangeEventHandler<HTMLInputElement>>(e => {
    setSelectedLanguage(e.currentTarget.value)
  }, [])

  const handleEmailChange: ChangeEventHandler<HTMLInputElement> = event => {
    setEmailValue(event.currentTarget.value)
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    // note: only fires for valid email
    e.preventDefault()
    if (!recaptchaToken) {
      setResultMessage(messages.missingRecaptcha ?? '')
      return
    }

    //submit to subscription service (which routes submission to Cision and MailChimp)
    setIsProcessing(true)
    setResultMessage('')
    fetch(subscriptionConfig.SubscriptionServiceUrl, {
      method: 'POST',
      headers: {
        Accept: '*/*',
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: getFormContent(),
    })
      .then(response => {
        if (response.ok) {
          setResultMessage(messages.success ?? '')
        } else {
          if (response.headers.has('x-result-code')) {
            const resultCode = parseInt(response.headers.get('x-result-code') ?? '0')

            if (resultCode === 1) {
              setResultMessage(messages.alreadySignedUp ?? '')
              return
            }
          }

          setResultMessage(messages.registrationFailure ?? '')
        }
      })
      .catch(error => {
        setResultMessage(messages.generalError ?? '')
        console.error(error)
      })
      .finally(() => {
        setIsProcessing(false)

        if (recaptchaRef.current) {
          recaptchaRef.current.reset()
        }
      })
  }

  // Ensure submit button is disabled at certain points
  // to avoid user spamming submit
  useEffect(() => {
    if (isProcessing) {
      setSubmitDisabled(true)
    }
  }, [isProcessing])

  useEffect(() => {
    if (!isNullOrWhiteSpace(countries?.selected)) {
      setSelectedCountry(countries?.selected)
    }
  }, [countries])

  // Re-enable submit when any input has changed
  useEffect(() => setSubmitDisabled(false), [filterState, emailValue, selectedLanguage])

  const newsTypes = IS_EQT_FOUNDATION
    ? []
    : [
        {
          label: translations.regulatoryNewsFilter,
          value: CisionFilterValues.regulatoryNews,
        },
        {
          label: translations.eqtNewsFilter,
          value: CisionFilterValues.eqtNews,
        },
      ]

  const languages = IS_EQT_GROUP
    ? [
        {
          label: translations.english,
          tooltip: translations.english,
          value: 'en',
        },
        {
          label: translations.swedish,
          tooltip: translations.swedish,
          value: 'sv',
        },
      ]
    : []

  const investor = isBrowser() ? getCookieValue('eqt_tsd_investor_type') : undefined

  useEffect(() => {
    if (!isNullOrWhiteSpace(investor)) {
      setSelectedInvestorType(investor)
    }
  }, [investor])

  const investorTypes =
    IS_EQT_NEXUS || IS_EQT_NEXUS_AU
      ? !isNullOrWhiteSpace(investor)
        ? [
            {
              label:
                investor === 'professional'
                  ? investors?.professionalInvestorLabel
                  : investors?.nonProfessionalInvestorLabel,
              value: investor,
            },
          ]
        : [
            {
              label: investors?.professionalInvestorLabel ?? '',
              value: 'Professional',
            },
            {
              label: investors?.nonProfessionalInvestorLabel ?? '',
              value: 'Non-professional',
            },
          ]
      : []

  return (
    <form
      action={subscriptionConfig.SubscriptionServiceUrl}
      method="POST"
      onSubmit={handleSubmit}
      className={classNames(themeSettings.textColor, 'font-medium')}
    >
      <div>
        {!!newsTypes && newsTypes.length > 0 && (
          <fieldset
            className={classNames(
              'flex gap-10 mb-30',
              themeSettings.radioCheckBoxAlignment
            )}
          >
            <legend className={classNames('font-t mb-10', themeSettings.textSize)}>
              {translations.newsFilterLabel}
            </legend>

            {newsTypes.map(chk => (
              <Checkbox
                additionalClassName="mb-10 mr-10 last:mb-0 last:mr-0 sm:mb-0"
                defaultChecked={filterState[chk.value]}
                key={chk.value}
                label={chk.label ?? ''}
                name={CisionFields.filter}
                onChange={handleFilterChange}
                value={chk.value}
                variant="button"
                color={themeSettings.theme === 'dark' ? 'primary' : 'default'}
              />
            ))}
          </fieldset>
        )}

        {!!languages && languages.length > 0 && (
          <fieldset
            className={classNames(
              'flex gap-10 mb-30',
              themeSettings.radioCheckBoxAlignment
            )}
          >
            <legend className={classNames('font-t mb-10', themeSettings.textSize)}>
              {translations.languageFilterLabel}
            </legend>

            {languages.map(rb => (
              <RadioButton
                additionalClassName="mr-10 last:mr-0 w-fit"
                checked={rb.value === selectedLanguage}
                key={rb.value}
                label={rb.label ?? ''}
                name={CisionFields.language}
                onChange={handleLanguageChange}
                tooltip={rb.tooltip ?? ''}
                value={rb.value}
                theme={themeSettings.theme}
              />
            ))}
          </fieldset>
        )}
        <div
          className={classNames('flex', {
            'flex-col sm:flex-row sm:gap-10': themeSettings.countryAndInvestorAsColumn,
            'flex-col': !themeSettings.countryAndInvestorAsColumn,
          })}
        >
          {investorTypes?.length > 0 && (
            <fieldset className="flex flex-wrap gap-10 mb-15">
              <legend className={classNames('font-t mb-10', themeSettings.textSize)}>
                {investors?.selectInvestorLabel ?? 'Investor type'}
              </legend>
              {investorTypes.length === 1 && <b>{investorTypes[0].label}</b>}
              {investorTypes.length === 1 && (
                <Input
                  displayValidationMessage={false}
                  type={'text'}
                  disabled={true}
                  hidden
                  additionalClassName="border-none"
                  name={CisionFields.investorType}
                  defaultValue={investorTypes[0].value}
                />
              )}
              {investorTypes.length > 1 &&
                investorTypes.map(option => (
                  <RadioButton
                    additionalClassName="mr-10 last:mr-0"
                    checked={option.value === selectedInvestorType}
                    key={option.value}
                    label={option.label ?? ''}
                    name={CisionFields.investorType}
                    onChange={() => setSelectedInvestorType(option.value)}
                    value={option.value}
                    theme={themeSettings.theme}
                    required
                  />
                ))}
            </fieldset>
          )}

          {countries?.items && countries.items.length > 0 && (
            <fieldset className="flex flex-wrap gap-10 mb-15">
              <legend className={classNames('font-t mb-10', themeSettings.textSize)}>
                {countries.label ?? 'Country of residence'}
              </legend>
              {countries.items.length === 1 && <b>{countries.items[0].label}</b>}
              {countries.items.length === 1 && (
                <Input
                  type={'text'}
                  hidden
                  displayValidationMessage={false}
                  additionalClassName="border-none"
                  disabled={true}
                  name={CisionFields.country}
                  defaultValue={countries.items[0].value}
                />
              )}
              {countries.items.length > 1 && (
                <Dropdown
                  name={CisionFields.country}
                  onChange={setSelectedCountry}
                  options={countries.items}
                  placeholder={countries.placeholder ?? 'Select country'}
                  value={selectedCountry}
                  variant="button"
                  required
                />
              )}
            </fieldset>
          )}
        </div>
      </div>

      {/* Start loading recaptcha scripts when the user viewport is within 500px of the element. */}
      <InView rootMargin="0px 0px 500px 0px" triggerOnce>
        {({ inView, ref }) => (
          <div className="mb-30 flex justify-center -mx-15 sm:block sm:mx-0" ref={ref}>
            {inView && (
              <ReCAPTCHA
                ref={recaptchaRef}
                onChange={token => {
                  setRecaptchaToken(token)
                  setSubmitDisabled(false)
                }}
                sitekey={subscriptionConfig.RecaptchaKey ?? 'missing key'}
                hl={translations.language ?? 'en'}
              />
            )}
          </div>
        )}
      </InView>

      <div className="relative">
        <label
          className={classNames('block font-t mb-10', themeSettings.textSize)}
          htmlFor={CisionFields.email}
        >
          {translations.emailLabel}
        </label>
        {themeSettings.theme === 'light' && (
          <Input
            name={CisionFields.email}
            onChange={handleEmailChange}
            placeholder={translations.emailPlaceholder ?? undefined}
            required
            type="email"
          >
            <Button
              additionalClassName="mr-10"
              disabled={submitDisabled}
              size="small"
              square
              theme={ButtonTheme.PrimaryInverted}
            >
              {translations.subscribeButtonLabel}
            </Button>
          </Input>
        )}
        {themeSettings.theme === 'dark' && (
          <div className="flex flex-col sm:flex-row gap-15">
            <Input
              name={CisionFields.email}
              onChange={handleEmailChange}
              placeholder={translations.emailPlaceholder ?? undefined}
              required
              type="email"
              displayValidationMessage={false}
              theme={themeSettings.theme}
            />
            <Button
              additionalClassName="mr-10 justify-center"
              disabled={submitDisabled}
              size="large"
              theme={
                themeSettings.theme !== 'dark'
                  ? ButtonTheme.PrimaryInverted
                  : ButtonTheme.SecondaryDark
              }
            >
              {translations.subscribeButtonLabel}
            </Button>
          </div>
        )}
        {!!disclaimer?.length && (
          <div
            className={classNames(
              'font-normal mb-20 md:mb-30 text-t-200',
              themeSettings.textColor
            )}
          >
            <RichTextArea
              value={disclaimer}
              components={{
                block: BasicBlockSerializer,
              }}
            />
          </div>
        )}
        {!isNullOrWhiteSpace(resultMessage) && (
          // Placeholder for short status messages
          <div
            className={classNames(
              'absolute w-full mt-8 text-center font-t',
              themeSettings.textSize
            )}
          >
            {resultMessage}
          </div>
        )}
      </div>
    </form>
  )
}
